/**
 * Every query key in the app, declared here rather than inline
 * (docs/07-implementation-roadmap.md §3).
 *
 * The API has no ETags and no optimistic-concurrency support, so refetching
 * after a write is the only correctness story available: a mutation states what
 * it invalidates, and that statement has to name keys that actually exist. Keys
 * scattered across features drift from each other by a typo and the screen
 * quietly shows stale data — which is why they live in one file.
 *
 * Shape: `['members']` invalidates everything for the resource, `['members',
 * 'list', params]` a specific page, `['members', 'detail', id]` one record.
 * Invalidating the resource root therefore catches both.
 */

/** URL query parameters as they are sent to the API — the names come from docs/04. */
export type ListParams = Record<string, string | number | boolean | undefined>;

function resource<const T extends string>(name: T) {
  return {
    all: [name] as const,
    lists: () => [name, 'list'] as const,
    list: (params: ListParams = {}) => [name, 'list', params] as const,
    details: () => [name, 'detail'] as const,
    detail: (id: string) => [name, 'detail', id] as const,
  };
}

export const queryKeys = {
  members: {
    ...resource('members'),
    statusHistory: (id: string) => ['members', 'detail', id, 'status-history'] as const,
    verifications: (id: string) => ['members', 'detail', id, 'verifications'] as const,
    documents: (id: string) => ['members', 'detail', id, 'documents'] as const,
    birthdays: (params: ListParams = {}) => ['members', 'birthdays', params] as const,
    anniversaries: (params: ListParams = {}) => ['members', 'anniversaries', params] as const,
  },
  families: {
    ...resource('families'),
    verifications: (id: string) => ['families', 'detail', id, 'verifications'] as const,
  },
  regions: resource('regions'),
  changeRequests: resource('change-requests'),
  /**
   * Intake drafts (docs/02 rule 18a). `resource()` would give a `list(params)`
   * this endpoint has no use for — a person's drafts are not filtered, they are
   * simply theirs.
   */
  intakeDrafts: {
    all: ['intake', 'drafts'] as const,
    list: () => ['intake', 'drafts', 'list'] as const,
    detail: (id: string) => ['intake', 'drafts', 'detail', id] as const,
  },
  users: resource('users'),
  auditLogs: resource('audit-logs'),
  /**
   * Reports (F6.1). Not `resource()` — there is no list and no record here, and
   * the two endpoints answer different questions over different populations, so
   * a shared `detail(id)` shape would only invite one to invalidate the other.
   *
   * `scope` is part of the key on purpose: a coordinator toggling between their
   * own region and church-wide is asking for two different populations, and
   * caching them under one key would show them the previous answer while the
   * new one loads (docs/04 §6a's `coverage` block exists for the same reason).
   */
  reports: {
    all: ['reports'] as const,
    membershipSummary: (params: ListParams = {}) => ['reports', 'membership-summary', params] as const,
    membershipTrend: (params: ListParams = {}) => ['reports', 'membership-trend', params] as const,
    verificationCoverage: (params: ListParams = {}) => ['reports', 'verification-coverage', params] as const,
    attestations: (params: ListParams = {}) => ['reports', 'attestations', params] as const,
    deaths: (params: ListParams = {}) => ['reports', 'deaths', params] as const,
  },
  church: {
    all: ['church'] as const,
    detail: () => ['church', 'detail'] as const,
    /**
     * Fetched once at boot and held for the session (F1.9). It is the only
     * settings endpoint a PASTOR or REGION_COORDINATOR may call, so nothing in
     * the shell may reach for `church.detail()` instead.
     */
    clientConfig: () => ['church', 'client-config'] as const,
    /**
     * The configurable permission grid (docs/07 P5). Under `church` so that
     * `invalidateQueries({ queryKey: church.all })` reaches it *and*
     * `clientConfig` — a saved permission change has to move both, or every
     * other screen keeps answering from the session-long overlay it cached at
     * boot while this screen shows the new value.
     */
    roleCapabilities: () => ['church', 'role-capabilities'] as const,
  },
} as const;
